import type { TDebouncedFn } from '@/types/debounce'

export function createDebounceFn<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => TResult | Promise<TResult>,
  ms: number,
): TDebouncedFn<TArgs, Awaited<TResult>> {
  let timer: ReturnType<typeof setTimeout> | null = null
  let pendingArgs: TArgs | null = null
  let settlePending: ((value: Awaited<TResult> | undefined) => void) | null = null
  let failPending: ((reason: unknown) => void) | null = null

  function clearPending() {
    if (timer !== null) {
      clearTimeout(timer)
    }
    timer = null
    pendingArgs = null
    settlePending = null
    failPending = null
  }

  async function run(): Promise<Awaited<TResult> | undefined> {
    if (pendingArgs === null) {
      return undefined
    }

    const args = pendingArgs
    const settle = settlePending
    const fail = failPending
    clearPending()

    try {
      const result = await fn(...args)
      settle?.(result)

      return result
    } catch (error) {
      fail?.(error)
      throw error
    }
  }

  function cancel() {
    const settle = settlePending
    clearPending()
    settle?.(undefined)
  }

  const debounced = (...args: TArgs): Promise<Awaited<TResult> | undefined> => {
    cancel()

    return new Promise((resolve, reject) => {
      pendingArgs = args
      settlePending = resolve
      failPending = reject
      timer = setTimeout(() => {
        run().catch(() => undefined)
      }, ms)
    })
  }

  return Object.assign(debounced, {
    cancel,
    flush: () => {
      if (pendingArgs === null) {
        return Promise.resolve(undefined)
      }

      return run()
    },
    isPending: () => pendingArgs !== null,
  })
}
